// birthdaysApi — adaptador del Panel de Cumpleaños (Inc12b) sobre lealify-base.
// Reemplaza: ElasticsearchService.searchBirthdayClients (lista), la config en
// businesses/{id}.birthdayAutoMessage (toggles) y el ENVÍO desde el navegador
// (useWahaSending). El envío ahora es SERVER-SIDE: POST /birthdays/send dispara
// el broker WhatsApp de base (con dedup anual + shadow). El template vive en
// settings.template (no en un editor con storageKey local). Imagen: settings.image_url
// (el subido se difiere, igual que en campañas/mensajes — base envía solo texto por ahora).
import { baseApi } from './baseClient';

export interface BirthdaySettings {
  autoMessageEnabled: boolean;
  template: string;
  imageUrl: string | null;
  notifyOwner: boolean;
  maxPerRun: number;
  // Regalo de puntos de cumpleaños (28-ago-2026). Abona saldo REAL del negocio el día del
  // cumpleaños, una sola vez al año, justo antes de mandar el mensaje (así @puntos ya sale
  // con el regalo dentro). Apagado por defecto; el backend topa el monto en 500.
  giftPointsEnabled: boolean;
  giftPoints: number;
}

// Contacto de cumpleaños tal como lo usa el panel (shape de la página).
export interface BirthdayContact {
  id: string;
  visitorName: string | null;
  phoneNumber: string;
  countryCode: string;
  birthDay: number | null;     // 1-31
  birthMonth: number | null;   // 0-11 (índice JS, como esperaba el panel)
  points: number;
  greeted: boolean;            // felicitado DE VERDAD este año (sent/shadow)
  sendStatus: SendStatus;      // estado del intento de este año (null = nunca se intentó)
  failReason: string | null;   // etiqueta corta del fallo, lista para pintar
  failDetail: string | null;   // detalle crudo del backend (tooltip / diagnóstico)
  envio: EnvioPreview | null;  // pronóstico del gate: ¿le saldrá su felicitación automática?
}

// Pronóstico del gate anti-ban para el envío automático (backend ?preview=1,
// solo lectura — 31-jul-2026). null = backend viejo o teléfono ausente.
export interface EnvioPreview {
  estado: 'sale' | 'frenado' | 'desconocido';
  via?: string;                // sale: 'conversacion' | 'vida_visita' | 'vida_cita' | 'vida_alta' | 'gate_off'
  dias_inbound?: number | null;
  motivo?: string;             // frenado: joven_frio | dormido_frio | nunca_cliente | …
  hasta?: string | null;       // YYYY-MM-DD en que se libera (frenos temporales)
  permanente?: boolean;
  overridable?: boolean;
}

// Estado del envío de este año, tal como lo guarda birthday_sends.
export type SendStatus = 'sent' | 'shadow' | 'failed' | null;

// Traduce el `detail` técnico del backend a algo que el operador entienda.
//   'whatsapp:tel-invalido …' → el teléfono guardado no puede existir (le faltan dígitos)
//   'whatsapp:sin-wa …'       → CONFIRMADO por lookup: el número no está en WhatsApp
//   'whatsapp:no-entregado …' → falló y no se pudo confirmar el porqué (no afirmamos de más)
//   'whatsapp:463-timelock …' → WhatsApp frenó a esta cuenta (se destraba con los días)
function failLabel(detail: string | null): string {
  const d = String(detail || '');
  if (d.includes('tel-invalido')) return 'Teléfono incompleto';
  if (d.includes('sin-wa')) return 'Sin WhatsApp';
  if (d.includes('no-entregado')) return 'No se entregó';
  if (d.includes('463-timelock')) return 'WhatsApp bloqueado';
  return 'No se envió';
}

// Qué le decimos al dueño del negocio cuando toca el chip. El `detail` crudo trae
// el JSON del error de WAHA (`{"statusCode":500,…"no LID found for 686…@s.whatsapp.net"}`),
// que no significa nada para quien atiende el mostrador y encima no dice qué hacer.
// El detalle técnico NO se pierde: sigue guardado en birthday_sends y se muestra
// aparte, plegado, para diagnóstico.
export function failHelp(detail: string | null): string {
  const d = String(detail || '');
  if (d.includes('tel-invalido')) {
    const m = d.match(/(\d+)\s*digitos/);
    const n = m ? `Solo tiene ${m[1]} dígitos` : 'Está incompleto';
    return `${n} y un número de celular necesita 10. Corrige el teléfono en la ficha del cliente y vuelve a intentarlo.`;
  }
  if (d.includes('sin-wa')) return 'Comprobamos el número y no tiene WhatsApp. Confirma con el cliente cuál es su número de WhatsApp y actualízalo en su ficha.';
  if (d.includes('463-timelock')) return 'WhatsApp frenó temporalmente los envíos de este negocio. Se destraba solo en unos días; no hace falta que hagas nada.';
  if (d.includes('no-entregado')) return 'No se pudo entregar y no logramos comprobar el motivo. Puedes reintentarlo con el botón de al lado.';
  return 'La felicitación no llegó a salir. Puedes reintentarlo con el botón de al lado.';
}

function mapSettings(s: any): BirthdaySettings {
  return {
    autoMessageEnabled: s?.auto_message_enabled === true,
    template: s?.template || '',
    imageUrl: s?.image_url || null,
    notifyOwner: s?.notify_owner !== false,
    maxPerRun: Number(s?.max_per_run) || 1,
    giftPointsEnabled: s?.gift_points_enabled === true,
    giftPoints: Number(s?.gift_points) || 0,
  };
}

function mapContact(r: any): BirthdayContact {
  // birth_month en base es 1-12 → el panel trabaja con índice 0-11.
  const bm = r.birth_month != null ? Number(r.birth_month) - 1 : null;
  // send_status solo existe desde el fix 24-jul-2026; si el backend aún no lo manda,
  // sendStatus queda null y el panel se comporta como antes (solo verde/pendiente).
  const sendStatus: SendStatus = r.send_status === 'sent' || r.send_status === 'shadow' || r.send_status === 'failed'
    ? r.send_status : null;
  const failed = sendStatus === 'failed';
  return {
    id: String(r.id),
    visitorName: [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || null,
    phoneNumber: r.phone || '',
    countryCode: '',
    birthDay: r.birth_day != null ? Number(r.birth_day) : null,
    birthMonth: bm,
    points: Number(r.points_balance) || 0,
    greeted: r.greeted === 1 || r.greeted === true || r.greeted === '1',
    sendStatus,
    failReason: failed ? failLabel(r.send_detail) : null,
    failDetail: failed ? (r.send_detail || null) : null,
    envio: r.envio || null,
  };
}

// ── Historial (7-sep-2026) ───────────────────────────────────────────────────
// Un cumpleaños de los últimos N días y qué pasó con él. sendStatus null = no salió,
// y `motivo` dice por qué (lo que el gate anti-ban dejó en wa_gate_log, o `sin_intento`
// cuando ni se intentó: automático apagado, sin plantilla…). Lo pidió Club W: «mi
// gerente quiere un control de a quiénes se les ha mandado».
export interface BirthdayHistoryRow extends BirthdayContact {
  bdayDate: string;            // YYYY-MM-DD del cumpleaños (con su año real, no el actual)
  sentAt: string | null;       // cuándo salió (birthday_sends.created_at)
  channel: string | null;      // 'whatsapp' | 'email' | 'both'
  motivo: string | null;       // solo sin envío: joven_frio | sesion_caida | dormido_frio | … | sin_intento
  motivoDetalle: string | null;
  marketingOptOut: boolean;
}

export const birthdaysApi = {
  getSettings: async (): Promise<{ settings: BirthdaySettings; yourLevel: string }> => {
    const r = await baseApi.get<{ settings: any; your_level: string }>('/birthdays/settings');
    return { settings: mapSettings(r.settings), yourLevel: r.your_level };
  },

  saveSettings: async (patch: Partial<BirthdaySettings>): Promise<BirthdaySettings> => {
    const body: Record<string, unknown> = {};
    if (patch.autoMessageEnabled !== undefined) body.auto_message_enabled = patch.autoMessageEnabled;
    if (patch.template !== undefined) body.template = patch.template;
    if (patch.imageUrl !== undefined) body.image_url = patch.imageUrl;
    if (patch.notifyOwner !== undefined) body.notify_owner = patch.notifyOwner;
    if (patch.maxPerRun !== undefined) body.max_per_run = patch.maxPerRun;
    if (patch.giftPointsEnabled !== undefined) body.gift_points_enabled = patch.giftPointsEnabled;
    if (patch.giftPoints !== undefined) body.gift_points = patch.giftPoints;
    const r = await baseApi.put<{ settings: any }>('/birthdays/settings', body);
    return mapSettings(r.settings);
  },

  // Próximos cumpleaños (ya excluye dados de baja de marketing en el backend).
  listUpcoming: async (days = 30): Promise<{ today: BirthdayContact[]; upcoming: BirthdayContact[] }> => {
    const r = await baseApi.get<{ today: { month: number; day: number }; contacts: any[] }>(`/birthdays?days=${days}&preview=1`);
    const all = (r.contacts || []).map(mapContact);
    // birth_month del backend es 1-12; today.month también 1-12.
    const tMonth = (r.today?.month ?? 0) - 1;
    const tDay = r.today?.day ?? 0;
    const today = all.filter((c) => c.birthMonth === tMonth && c.birthDay === tDay);
    const upcoming = all.filter((c) => !(c.birthMonth === tMonth && c.birthDay === tDay));
    return { today, upcoming };
  },

  // Historial de los últimos N días (7-180): enviadas y no enviadas con motivo.
  listHistory: async (days = 60): Promise<{ days: number; rows: BirthdayHistoryRow[] }> => {
    const r = await baseApi.get<{ days: number; rows: any[] }>(`/birthdays/history?days=${days}`);
    const rows: BirthdayHistoryRow[] = (r.rows || []).map((x) => ({
      ...mapContact(x),
      bdayDate: String(x.bday_date || ''),
      sentAt: x.sent_at ? String(x.sent_at) : null,
      channel: x.channel ? String(x.channel) : null,
      motivo: x.send_status ? null : (x.motivo || 'sin_intento'),
      motivoDetalle: x.motivo_detalle || null,
      marketingOptOut: x.marketing_opt_out === 1 || x.marketing_opt_out === true,
    }));
    return { days: Number(r.days) || days, rows };
  },

  // Felicita a UN contacto, server-side. force = reenviar aunque ya se haya felicitado este año.
  send: (contactId: string, force = false): Promise<{ ok: boolean; status?: string; shadow?: boolean; message?: string; reason?: string }> =>
    baseApi.post('/birthdays/send', { contact_id: Number(contactId), force }),

  // Corre el cron de cumpleaños para este negocio a demanda (envío masivo escalonado).
  runNow: (): Promise<{ summary: unknown }> => baseApi.post('/birthdays/run', {}),
};

export default birthdaysApi;
